import React, { useState, useEffect, useCallback } from 'react';
import { Users, Shield, Trash2, Save, RefreshCw, UserCog, Search, KeyRound, Eye, EyeOff, UserCheck, UserX, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  getUsers, updateUserPermisos, updateUserRole, deleteUser, getModulosDisponibles,
  approveUser, rejectUser,
  getSuperCodigo, updateSuperCodigo,
} from '../lib/api';
import { useAuth } from '../lib/auth';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Administrador',
  usuario: 'Usuario',
};

const ROLE_COLORS = {
  super_admin: 'destructive',
  admin: 'default',
  usuario: 'secondary',
};

export default function GestionUsuarios() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Edit dialog
  const [editingUser, setEditingUser] = useState(null);
  const [editPermisos, setEditPermisos] = useState([]);
  const [editRole, setEditRole] = useState('usuario');
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletingInProgress, setDeletingInProgress] = useState(false);

  // Super Código (Configuración de Seguridad)
  const [superCodigo, setSuperCodigo] = useState('');
  const [nuevoSuperCodigo, setNuevoSuperCodigo] = useState('');
  const [showSuperCodigo, setShowSuperCodigo] = useState(false);
  const [savingCodigo, setSavingCodigo] = useState(false);
  const [codigoEsDefault, setCodigoEsDefault] = useState(false);

  const loadSuperCodigo = useCallback(async () => {
    try {
      const res = await getSuperCodigo();
      setSuperCodigo(res.data?.valor || '');
      setCodigoEsDefault(!!res.data?.es_default);
    } catch (err) {
      // Si el usuario no es super_admin, simplemente no carga
      console.warn('No se pudo cargar el super código:', err.response?.data?.detail);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, modulosRes] = await Promise.all([
        getUsers(),
        getModulosDisponibles(),
      ]);
      setUsers(usersRes.data || []);
      setModulos(modulosRes.data?.modulos || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); loadSuperCodigo(); }, [loadData, loadSuperCodigo]);

  const openEdit = (u) => {
    setEditingUser(u);
    setEditPermisos(u.modulos_permitidos || []);
    setEditRole(u.role || 'usuario');
  };

  // Marca especial "_conteo_ciego": el usuario NO ve las cifras del sistema
  // (existencias, columnas Sistema/Diferencia) en Almacén e Inventario.
  const CONTEO_CIEGO_KEY = '_conteo_ciego';
  const editConteoCiego = editPermisos.includes(CONTEO_CIEGO_KEY);
  const toggleConteoCiego = () => {
    setEditPermisos((prev) =>
      prev.includes(CONTEO_CIEGO_KEY)
        ? prev.filter((k) => k !== CONTEO_CIEGO_KEY)
        : [...prev, CONTEO_CIEGO_KEY],
    );
  };

  const togglePermiso = (key) => {
    setEditPermisos((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    // Solo agrega módulos reales, preservando flags especiales (ej. _conteo_ciego)
    const reales = modulos.map((m) => m.key).filter((k) => !String(k).startsWith('_'));
    setEditPermisos((prev) => {
      const flagsActuales = prev.filter((k) => String(k).startsWith('_'));
      return [...reales, ...flagsActuales];
    });
  };

  const handleClearAll = () => {
    // Solo limpia módulos reales, preserva flags especiales
    setEditPermisos((prev) => prev.filter((k) => String(k).startsWith('_')));
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      // Si se cambió el rol, actualizar primero
      if (editRole !== editingUser.role) {
        await updateUserRole(editingUser.id, editRole);
      }
      // Actualizar permisos
      await updateUserPermisos(editingUser.id, editPermisos);
      toast.success(`Permisos de ${editingUser.email} actualizados`);
      setEditingUser(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    setDeletingInProgress(true);
    try {
      await deleteUser(deletingUser.id);
      toast.success('Usuario eliminado');
      setDeletingUser(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando usuario');
    } finally {
      setDeletingInProgress(false);
    }
  };

  const handleSaveSuperCodigo = async () => {
    const valor = nuevoSuperCodigo.trim();
    if (valor.length < 4) {
      toast.error('El código debe tener al menos 4 caracteres');
      return;
    }
    if (valor === superCodigo) {
      toast.info('El nuevo código es igual al actual');
      return;
    }
    setSavingCodigo(true);
    try {
      const res = await updateSuperCodigo(valor);
      toast.success(res.data?.message || 'Código de Super Usuario actualizado');
      if (res.data?.warning === 'no_db_table') {
        toast.warning('Tabla "configuracion" no existe en Supabase. El código solo se guardó en memoria; ejecuta supabase_migrations_v6.sql para persistirlo.', { duration: 8000 });
      }
      setSuperCodigo(valor);
      setNuevoSuperCodigo('');
      setCodigoEsDefault(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando el código');
    } finally {
      setSavingCodigo(false);
    }
  };

  // Filtro de búsqueda — separa usuarios pendientes de activos
  const matchesSearch = (u) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase().trim();
    return (
      (u.email || '').toLowerCase().includes(term) ||
      (u.nombre || '').toLowerCase().includes(term)
    );
  };
  const usersPendientes = users.filter((u) => (u.estado || 'aprobado').toLowerCase() === 'pendiente');
  const usersActivos = users.filter((u) => (u.estado || 'aprobado').toLowerCase() !== 'pendiente');
  const usersFiltered = usersActivos.filter(matchesSearch);
  const pendientesFiltered = usersPendientes.filter(matchesSearch);

  // Aprobar / Rechazar
  const [actioningUserId, setActioningUserId] = useState(null);
  const handleApprove = async (u) => {
    setActioningUserId(u.id);
    try {
      await approveUser(u.id);
      toast.success(`Usuario aprobado: ${u.email}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error aprobando usuario');
    } finally {
      setActioningUserId(null);
    }
  };
  const handleReject = async (u) => {
    setActioningUserId(u.id);
    try {
      await rejectUser(u.id);
      toast.success(`Usuario rechazado: ${u.email}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error rechazando usuario');
    } finally {
      setActioningUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-gestion-usuarios">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <UserCog className="w-6 h-6" /> Gestión de Usuarios
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Administra los módulos y roles que cada usuario puede acceder.
          </p>
        </div>
        <Button variant="outline" onClick={loadData} data-testid="btn-refresh-users">
          <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
        </Button>
      </div>

      <Tabs defaultValue="activos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="activos" className="gap-2" data-testid="tab-usuarios-activos">
            <Users className="w-4 h-4" /> Usuarios activos
            <Badge variant="secondary" className="ml-1 tabular-nums">{usersActivos.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="pendientes" className="gap-2" data-testid="tab-usuarios-pendientes">
            <Clock className="w-4 h-4" /> Pendientes de aprobación
            {usersPendientes.length > 0 && (
              <Badge variant="destructive" className="ml-1 tabular-nums">{usersPendientes.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activos">

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Users className="w-5 h-5" /> Usuarios del Sistema ({users.length})
          </CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Buscar por nombre o correo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
              data-testid="input-buscar-usuario"
            />
          </div>
        </CardHeader>
        <CardContent>
          {usersFiltered.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{search.trim() ? 'Sin resultados' : 'No hay usuarios registrados'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="tabla-usuarios">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead className="text-center">Módulos</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersFiltered.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    const modulosReales = (u.modulos_permitidos || []).filter((k) => !String(k).startsWith('_'));
                    const totalModulos = modulosReales.length;
                    const isFullAccess = u.role === 'super_admin' || u.role === 'admin';
                    const isConteoCiegoUser = u.role !== 'super_admin' && (u.modulos_permitidos || []).includes('_conteo_ciego');
                    return (
                      <TableRow key={u.id} className="hover:bg-accent/60 transition-colors">
                        <TableCell className="font-medium">
                          {u.nombre || '—'}
                          {isSelf && <Badge variant="outline" className="ml-2 text-xs">Tú</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge variant={ROLE_COLORS[u.role] || 'outline'} className="text-xs">
                              <Shield className="w-3 h-3 mr-1" />
                              {ROLE_LABELS[u.role] || u.role}
                            </Badge>
                            {isConteoCiegoUser && (
                              <Badge
                                variant="outline"
                                className="text-[10px] gap-1 bg-primary/10 text-primary border-primary/30"
                                title="Modo Conteo Ciego activo"
                                data-testid={`badge-conteo-ciego-${u.id}`}
                              >
                                <EyeOff className="w-3 h-3" /> Ciego
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {isFullAccess ? (
                            <Badge variant="default" className="text-xs">Acceso total</Badge>
                          ) : totalModulos === 0 ? (
                            <Badge variant="secondary" className="text-xs">Sin módulos</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">{totalModulos} módulos</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(u)}
                            data-testid={`btn-edit-user-${u.id}`}
                          >
                            <UserCog className="w-4 h-4 mr-1" /> Editar
                          </Button>
                          {!isSelf && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeletingUser(u)}
                              title="Eliminar"
                              data-testid={`btn-delete-user-${u.id}`}
                              className="ml-1"
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="pendientes">
          <Card data-testid="card-usuarios-pendientes">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Solicitudes de Acceso
                {usersPendientes.length > 0 && (
                  <Badge variant="destructive" className="ml-1 tabular-nums">
                    {pendientesFiltered.length}{search.trim() ? ` de ${usersPendientes.length}` : ''}
                  </Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Los usuarios nuevos que se registran quedan aquí hasta que tú los apruebes.
                Hasta entonces NO pueden iniciar sesión.
              </p>
            </CardHeader>
            <CardContent>
              {pendientesFiltered.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>{search.trim() ? 'Sin resultados' : 'No hay solicitudes pendientes'}</p>
                  <p className="text-xs mt-1">Cuando alguien se registre con un email aparecerá aquí.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="tabla-pendientes">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Correo</TableHead>
                        <TableHead>Solicitado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendientesFiltered.map((u) => {
                        const fecha = u.created_at
                          ? new Date(u.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '—';
                        const busy = actioningUserId === u.id;
                        return (
                          <TableRow key={u.id} className="hover:bg-amber-50/40 dark:hover:bg-amber-950/20 transition-colors">
                            <TableCell className="font-medium">{u.nombre || '—'}</TableCell>
                            <TableCell className="text-sm">{u.email}</TableCell>
                            <TableCell className="text-sm text-muted-foreground tabular-nums">{fecha}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                onClick={() => handleApprove(u)}
                                disabled={busy}
                                className="mr-2"
                                data-testid={`btn-aprobar-${u.id}`}
                              >
                                <UserCheck className="w-4 h-4 mr-1.5" /> Aprobar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReject(u)}
                                disabled={busy}
                                className="text-destructive hover:text-destructive"
                                data-testid={`btn-rechazar-${u.id}`}
                              >
                                <UserX className="w-4 h-4 mr-1.5" /> Rechazar
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== CONFIGURACIÓN DE SEGURIDAD: Super Código ===== */}
      <Card data-testid="card-super-codigo">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <KeyRound className="w-5 h-5" /> Código de Super Usuario
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Este código se solicita al editar o eliminar registros sensibles (ej. Producción Diaria).
            Compártelo solo con personas autorizadas.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Código actual */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Código actual</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  type={showSuperCodigo ? 'text' : 'password'}
                  value={superCodigo || '••••••••'}
                  readOnly
                  className="h-11 font-mono tracking-wider"
                  data-testid="input-super-codigo-actual"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowSuperCodigo((v) => !v)}
                  title={showSuperCodigo ? 'Ocultar' : 'Mostrar'}
                  data-testid="btn-toggle-super-codigo"
                  className="h-11 w-11 shrink-0"
                >
                  {showSuperCodigo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              {codigoEsDefault && (
                <p className="text-[11px] text-amber-600 dark:text-amber-500 mt-1.5 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Estás usando el código por defecto. Te recomendamos cambiarlo.
                </p>
              )}
            </div>

            {/* Cambiar código */}
            <div>
              <Label htmlFor="input-nuevo-codigo" className="text-xs uppercase tracking-wide text-muted-foreground">
                Cambiar código
              </Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  id="input-nuevo-codigo"
                  type="text"
                  value={nuevoSuperCodigo}
                  onChange={(e) => setNuevoSuperCodigo(e.target.value)}
                  placeholder="Nuevo código (mín. 4 caracteres)"
                  className="h-11 font-mono"
                  maxLength={64}
                  data-testid="input-nuevo-super-codigo"
                />
                <Button
                  onClick={handleSaveSuperCodigo}
                  disabled={savingCodigo || nuevoSuperCodigo.trim().length < 4}
                  data-testid="btn-guardar-super-codigo"
                  className="h-11 shrink-0"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingCodigo ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Al cambiarlo, todos los usuarios deberán usar el nuevo código.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== EDIT USER DIALOG ===== */}
      <Dialog open={!!editingUser} onOpenChange={(o) => { if (!o) setEditingUser(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-edit-user">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>
              Editar Usuario: {editingUser?.nombre || editingUser?.email}
            </DialogTitle>
            <DialogDescription>
              Configura el rol y los módulos a los que tendrá acceso este usuario.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div>
              <Label>Correo</Label>
              <Input value={editingUser?.email || ''} disabled className="h-10 mt-1.5" />
            </div>

            <div>
              <Label htmlFor="role-select">Rol</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger id="role-select" className="h-10 mt-1.5" data-testid="select-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usuario">Usuario — acceso según módulos</SelectItem>
                  <SelectItem value="admin">Administrador — acceso total (excepto Gestión de Usuarios)</SelectItem>
                  <SelectItem value="super_admin">Super Admin — acceso total + gestionar usuarios</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Los roles <strong>admin</strong> y <strong>super_admin</strong> tienen acceso automático a todos los módulos.
              </p>
            </div>

            {/* Modo Conteo Ciego (aplica a todos los roles excepto super_admin) */}
            {editRole !== 'super_admin' && (
              <div className="p-4 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="label-conteo-ciego">
                  <Checkbox
                    checked={editConteoCiego}
                    onCheckedChange={toggleConteoCiego}
                    className="mt-0.5"
                    data-testid="check-conteo-ciego"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <EyeOff className="w-4 h-4 text-primary" />
                      <span className="font-medium text-sm">Modo Conteo Ciego</span>
                      {editConteoCiego && (
                        <Badge variant="default" className="text-[10px] gap-1 bg-primary/15 text-primary border-primary/30 hover:bg-primary/15">
                          Activo
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Al activar, este usuario <strong>NO verá</strong> las cifras del sistema mientras
                      hace conteos: se ocultan las tarjetas &quot;Existencias en Almacén&quot; y las columnas
                      <em> Sistema</em> y <em>Diferencia</em> en Conteo Semanal y Conteo Diario. Solo
                      podrá ingresar lo que contó físicamente, sin poder comparar. Útil para evitar sesgo
                      del contador. Super Admin nunca se ve afectado.
                    </p>
                  </div>
                </label>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Módulos Permitidos</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={handleSelectAll} data-testid="btn-select-all-modulos">
                    Seleccionar todos
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={handleClearAll} data-testid="btn-clear-all-modulos">
                    Limpiar
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-accent/30 rounded-lg">
                {modulos
                  .filter((m) => !String(m.key).startsWith('_'))
                  .map((m) => {
                  const checked = editPermisos.includes(m.key);
                  const isSubPermiso = m.group === 'almacen_sub';
                  return (
                    <label
                      key={m.key}
                      className={`flex items-center gap-2 p-2 rounded-md hover:bg-background cursor-pointer transition-colors ${isSubPermiso ? 'pl-6 border-l-2 border-primary/40' : ''}`}
                      data-testid={`label-modulo-${m.key}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => togglePermiso(m.key)}
                        data-testid={`check-modulo-${m.key}`}
                      />
                      <span className={`text-sm ${isSubPermiso ? 'font-normal text-muted-foreground' : 'font-medium'}`}>
                        {m.label}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {editPermisos.filter((k) => !String(k).startsWith('_')).length} módulo(s) seleccionado(s)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="btn-save-user">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DELETE CONFIRM ===== */}
      <AlertDialog open={!!deletingUser} onOpenChange={(o) => { if (!o) setDeletingUser(null); }}>
        <AlertDialogContent data-testid="confirmar-eliminar-usuario">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario {deletingUser?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El usuario perderá acceso al sistema permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingInProgress}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deletingInProgress}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirmar-eliminar-usuario"
            >
              {deletingInProgress ? 'Eliminando...' : 'Eliminar Usuario'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
